import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn, getLastSegment } from '@/lib/utils'
import { useIsBuy1Get1ValueMatchCampaignActive } from '@/lib/utils/campaign/campaign-active'
import { TMenuMobileSectionItem } from '@/types/header'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'

type Props = {
  title: string
  list: TMenuMobileSectionItem[]
  type: string
  gaType?: string
}

export const MobileNavList: React.FC<Props> = ({ title, list, type, gaType }) => {
  const trackingType = gaType ?? type
  const t = useTranslations('header')
  const isBlackFridayActive = useIsBuy1Get1ValueMatchCampaignActive()

  // Tạo Black Friday item tự động khi campaign active
  const blackFridayItem: TMenuMobileSectionItem | null = isBlackFridayActive
    ? {
        title: 'nav.item.black_friday',
        href: '/black-friday',
      }
    : null

  const renderMenuItem = (
    listItem: TMenuMobileSectionItem,
    isBlackFriday: boolean = false
  ) => {
    const href = isBlackFriday ? '/black-friday' : listItem.href

    return (
      <Link
        href={href}
        aria-disabled={listItem.isComingSoon}
        tabIndex={listItem.isComingSoon ? -1 : 0}
        key={isBlackFriday ? 'black-friday' : listItem.href}
        className={cn(
          isBlackFriday
            ? 'flex w-full flex-col rounded-lg bg-gradient-to-r from-black to-neutral-900 px-4 py-3 font-sans text-base font-bold uppercase leading-5.5 text-white shadow-lg transition-all hover:scale-[1.02] hover:shadow-xl'
            : 'inline-block w-full rounded px-3 py-2 font-sans text-sm leading-5.5 text-neutral-600 hover:text-primary',
          !isBlackFriday &&
            listItem.img &&
            'flex items-center gap-2 border-b border-b-neutral-100',
          !isBlackFriday && listItem.className,
          listItem.isComingSoon &&
            'pointer-events-none cursor-default text-neutral-400 hover:text-neutral-400'
        )}
        ga-tracking-value={`menu__${trackingType}__${isBlackFriday ? 'black_friday' : getLastSegment(listItem.title)}`}
        ga-tracking-label={
          isBlackFriday
            ? t.rich('nav.item.black_friday', {
                br: () => <br className='hidden' />,
              })
            : t(listItem.title)
        }
        onClick={(e) => listItem.isComingSoon && e.preventDefault()}
      >
        {listItem.img && !isBlackFriday && (
          <Image
            src={ImageHelper.getImageUrl(listItem.img, '', 88)}
            width={44}
            height={44}
            alt={listItem.title}
            className='rounded-full'
          />
        )}{' '}
        {isBlackFriday ? (
          <div className='flex flex-col gap-1'>
            <span className='w-fit bg-black text-base leading-tight text-[#d6ff01]'>
              {t.rich('nav.item.black_friday', {
                br: () => <br className='hidden' />,
              })}
            </span>
          </div>
        ) : (
          <p className={cn(listItem.img && 'flex-1 py-3')}>
            {t(listItem.title)}
            {listItem.subtitle && <i> {t(listItem.subtitle)}</i>}
          </p>
        )}
      </Link>
    )
  }

  // Nếu chỉ có 1 item (không tính Black Friday), hiển thị như link trực tiếp
  const hasOnlyOneItem = list.length === 1 && !blackFridayItem
  const singleItem = hasOnlyOneItem ? list[0] : null

  if (hasOnlyOneItem && singleItem) {
    return (
      <Link
        href={singleItem.href}
        aria-disabled={singleItem.isComingSoon}
        tabIndex={singleItem.isComingSoon ? -1 : 0}
        className={cn(
          'block w-full rounded py-3 font-sans text-sm font-bold uppercase text-neutral-900 hover:text-primary hover:no-underline',
          singleItem.className,
          singleItem.isComingSoon &&
            'pointer-events-none cursor-default text-neutral-400 hover:text-neutral-400'
        )}
        ga-tracking-value={`menu__${trackingType}__${getLastSegment(singleItem.title)}`}
        ga-tracking-label={t(singleItem.title)}
        onClick={(e) => singleItem.isComingSoon && e.preventDefault()}
      >
        {t(singleItem.title)}
        {singleItem.subtitle && <i> {t(singleItem.subtitle)}</i>}
      </Link>
    )
  }

  return (
    <AccordionItem value={title} className='space-y-2 border-0'>
      <AccordionTrigger
        className='py-3 font-sans text-sm font-bold text-neutral-900 hover:no-underline'
        // ga-tracking-value={`menu__${getLastSegment(title)}`}
        // ga-tracking-label={t(title)}
      >
        {t(title)}
      </AccordionTrigger>
      <AccordionContent>
        {/* Hiển thị Black Friday item trước, nổi bật */}
        {blackFridayItem && (
          <div className='mb-4 space-y-2'>
            {renderMenuItem(blackFridayItem, true)}
          </div>
        )}
        {/* Hiển thị các items thường */}
        {list.map((item) => renderMenuItem(item, false))}
      </AccordionContent>
    </AccordionItem>
  )
}
